import { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Check, Minus, Building2, Home, TrendingUp, Zap, Users, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router';
import { getFeaturesByOutcome } from '@/lib/plan-gating';
import { trackTrialClick, trackUpgradeClick } from '@/lib/analytics';

gsap.registerPlugin(ScrollTrigger);

const plans = [
  {
    key: 'starter' as const, name: 'Starter', price: '$39', period: '/mo',
    subtitle: 'For testing one county',
    outcome: 'Validate before you scale',
    searches: '25', counties: '1 county',
    color: '#2F80ED', icon: Home,
  },
  {
    key: 'investor' as const, name: 'Investor', price: '$79', period: '/mo',
    subtitle: 'For active deal hunters',
    outcome: 'Find 200 parcels/mo across 10 counties',
    searches: '200', counties: '10 counties',
    color: '#7CFF6B', icon: TrendingUp, popular: true,
  },
  {
    key: 'pro' as const, name: 'Pro', price: '$129', period: '/mo',
    subtitle: 'For portfolio managers',
    outcome: '500 parcels + AI deal discovery',
    searches: '500', counties: '25 counties',
    color: '#7CFF6B', icon: Zap,
  },
  {
    key: 'enterprise' as const, name: 'Enterprise', price: 'Custom', period: '',
    subtitle: 'For acquisition teams',
    outcome: 'Unlimited + API + team seats',
    searches: 'Unlimited', counties: 'Unlimited',
    color: '#A855F7', icon: Building2,
  },
];

// Outcome categories with their features per tier
const outcomes = getFeaturesByOutcome();
const outcomeOrder = ['Find Better Deals', 'Reduce Risk', 'Save Time', 'Monitor Investments', 'Team Collaboration', 'Enterprise Management'];

export default function Pricing() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const items = section.querySelectorAll('.pricing-animate');
    const tl = gsap.timeline({ scrollTrigger: { trigger: section, start: 'top 75%', once: true } });
    tl.fromTo(items, { y: 30, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6, stagger: 0.08, ease: 'power3.out' });
    return () => { tl.kill(); };
  }, []);

  return (
    <section id="pricing" ref={sectionRef} className="relative py-20 md:py-28" style={{ background: '#0F1923' }}>
      <div className="mx-auto max-w-[1200px] px-6">

        {/* ── Section Header ── */}
        <div className="pricing-animate mb-12 text-center">
          <span className="label-accent mb-4 block" style={{ color: '#7CFF6B' }}>PRICING</span>
          <h2 className="font-display font-semibold" style={{ fontSize: 'clamp(32px, 4vw, 48px)', lineHeight: 1.1, color: '#F7F8F4' }}>
            One deal pays for your subscription.
          </h2>
          <p className="font-body mx-auto mt-4" style={{ fontSize: 17, color: '#a9b4c0', maxWidth: 560 }}>
            Start researching parcels immediately with full access during your trial.
            Upgrade when you are ready for unlimited exports and advanced scoring.
          </p>
          <div className="mt-4 flex items-center justify-center gap-2">
            <Users size={14} className="text-emerald-400" />
            <span className="text-xs" style={{ color: '#A8B8AE' }}>Land investing tools built for serious investors</span>
          </div>
        </div>

        {/* ── Mobile: Plan Cards ── */}
        <div className="grid gap-4 md:hidden pricing-animate mb-8">
          {plans.map(p => (
            <div key={p.key} className="rounded-2xl border p-5 relative" style={{ borderColor: p.popular ? '#7CFF6B' : 'rgba(255,255,255,0.1)', background: p.popular ? 'rgba(199,164,91,0.04)' : '#101D29' }}>
              {p.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#7CFF6B] px-3 py-1 text-[9px] font-black text-[#07110D]">
                  MOST POPULAR
                </div>
              )}
              <div className="flex items-center gap-2 mb-2">
                <p.icon size={16} style={{ color: p.color }} />
                <span className="font-body text-sm font-black" style={{ color: '#F7F8F4' }}>{p.name}</span>
              </div>
              <p className="font-display text-3xl font-semibold" style={{ color: '#F7F8F4' }}>{p.price}<span className="text-sm font-body font-normal" style={{ color: '#a9b4c0' }}>{p.period}</span></p>
              <p className="text-xs font-bold mt-1" style={{ color: p.color }}>{p.subtitle}</p>
              <p className="text-xs mt-2 mb-4" style={{ color: '#a9b4c0' }}>{p.outcome}</p>
              <button onClick={() => { trackTrialClick('pricing'); trackUpgradeClick(p.key); navigate('/register'); }} className="w-full rounded-xl py-2.5 text-xs font-black transition hover:brightness-105" style={{ background: p.popular ? '#7CFF6B' : 'transparent', color: p.popular ? '#07110D' : '#F7F8F4', border: p.popular ? 'none' : '1px solid rgba(255,255,255,0.2)' }}>
                {p.key === 'enterprise' ? 'Contact Sales' : 'Start 7-Day Free Trial'}
              </button>
            </div>
          ))}
        </div>

        {/* ── Desktop: Full Comparison Table ── */}
        <div className="hidden md:block pricing-animate mb-8">
          <div className="rounded-2xl border border-white/[0.08] overflow-hidden" style={{ background: '#101D29' }}>
            {/* Plan headers */}
            <div className="grid grid-cols-5 gap-0">
              <div className="p-4 border-b border-r border-white/[0.08]">
                <p className="text-[11px] font-bold text-[#8A9BAE] uppercase tracking-wider">What You Get</p>
              </div>
              {plans.map(p => (
                <div key={p.key} className={`p-4 border-b border-white/[0.08] text-center relative ${p.popular ? 'bg-[rgba(199,164,91,0.04)]' : ''}`}>
                  {p.popular && (
                    <div className="absolute -top-0 left-0 right-0 bg-[#7CFF6B] py-0.5">
                      <p className="text-[8px] font-black text-[#07110D] uppercase tracking-widest">Most Popular</p>
                    </div>
                  )}
                  <div className={`flex items-center justify-center gap-2 mb-1 ${p.popular ? 'mt-3' : ''}`}>
                    <p.icon size={14} style={{ color: p.color }} />
                    <span className="text-sm font-bold" style={{ color: '#F7F8F4' }}>{p.name}</span>
                  </div>
                  <p className="font-display text-xl font-semibold" style={{ color: '#F7F8F4' }}>{p.price}<span className="text-xs font-body font-normal" style={{ color: '#a9b4c0' }}>{p.period}</span></p>
                  <p className="text-[10px] mt-1" style={{ color: p.color }}>{p.searches} searches/mo</p>
                </div>
              ))}
            </div>

            {/* Features grouped by OUTCOME */}
            {outcomeOrder.map(outcomeName => {
              const features = outcomes[outcomeName];
              if (!features) return null;

              return (
                <div key={outcomeName}>
                  {/* Outcome header */}
                  <div className="grid grid-cols-5 gap-0">
                    <div className="col-span-5 p-2.5 border-b border-white/[0.06]" style={{ background: 'rgba(199,164,91,0.03)' }}>
                      <span className="text-[10px] font-black text-[#7CFF6B] uppercase tracking-wider">{outcomeName}</span>
                    </div>
                  </div>
                  {/* Feature rows */}
                  {features.map((feature, i) => (
                    <div key={i} className="grid grid-cols-5 gap-0">
                      <div className="p-3 border-b border-r border-white/[0.06]">
                        <p className="text-xs font-medium text-[#F7F8F4]">{feature.name}</p>
                        <p className="text-[10px] mt-0.5" style={{ color: '#6B7B8D' }}>{feature.roi}</p>
                      </div>
                      {(['starter', 'investor', 'pro', 'enterprise'] as const).map(tier => {
                        const tiers = ['starter', 'investor', 'pro', 'enterprise'];
                        const hasAccess = tiers.indexOf(tier) >= tiers.indexOf(feature.tier);
                        const tierColor = tier === 'starter' ? '#2F80ED' : tier === 'pro' ? '#7CFF6B' : tier === 'enterprise' ? '#A855F7' : '#7CFF6B';
                        return (
                          <div key={tier} className={`p-3 border-b border-white/[0.06] flex items-center justify-center ${tier === 'investor' && hasAccess ? 'bg-[rgba(199,164,91,0.02)]' : ''}`}>
                            {hasAccess ? (
                              <Check size={14} style={{ color: tierColor }} />
                            ) : (
                              <Minus size={12} style={{ color: '#A8B8AE' }} />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              );
            })}

            {/* CTA row */}
            <div className="grid grid-cols-5 gap-0">
              <div className="p-4 border-r border-white/[0.08]" />
              <div className="p-4 text-center"><button onClick={() => { trackTrialClick('pricing'); trackUpgradeClick('starter'); navigate('/register'); }} className="w-full rounded-xl border border-white/20 py-2.5 text-xs font-black transition hover:bg-white/5" style={{ color: '#F7F8F4' }}>Start Free Trial</button></div>
              <div className="p-4 text-center bg-[rgba(199,164,91,0.04)]"><button onClick={() => { trackTrialClick('pricing'); trackUpgradeClick('investor'); navigate('/register'); }} className="w-full rounded-xl py-2.5 text-xs font-black transition hover:brightness-105 flex items-center justify-center gap-1" style={{ background: '#7CFF6B', color: '#07110D' }}>Start Free Trial <ArrowRight size={12} /></button></div>
              <div className="p-4 text-center"><button onClick={() => { trackTrialClick('pricing'); trackUpgradeClick('pro'); navigate('/register'); }} className="w-full rounded-xl border border-white/20 py-2.5 text-xs font-black transition hover:bg-white/5" style={{ color: '#F7F8F4' }}>Start Free Trial</button></div>
              <div className="p-4 text-center"><button onClick={() => navigate('/contact')} className="w-full rounded-xl border py-2.5 text-xs font-black transition hover:brightness-105" style={{ borderColor: '#A855F7', color: '#A855F7' }}>Contact Sales</button></div>
            </div>
          </div>
        </div>

        {/* ── Trust Footer ── */}
        <div className="pricing-animate text-center space-y-2">
          <p className="text-xs" style={{ color: '#a9b4c0' }}>
            Starter, Investor, and Pro plans include a <span className="font-bold text-[#7CFF6B]">7-day free trial</span>. Enterprise is custom pricing.
          </p>
          <p className="text-[11px]" style={{ color: '#8A9BAE' }}>
            Cancel anytime. All plans include full access during the trial. You will not be charged until day 8.
          </p>
        </div>
      </div>
    </section>
  );
}
